import React, { useState } from "react"
import styled from "styled-components"

import Col from "react-bootstrap/Col"
import Dropdown from "react-bootstrap/Dropdown"

import { ITheme } from "../../../types/project"
import { IEvent } from "../../../types/event"
import ThumbsProject from "./ThumbsProject"
import ThumbsEvent from "./ThumbsEvent"
import { burger } from "../../../assets"
import { IFilter } from "../../../types/type"
import media from "../../../helpers/media"

const Header = styled.header`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid var(--color-grey-3);
  margin: 0;
  padding: 0px 0px 10px 0px;
  height: 50px;
  display: flex;
  align-items: flex-end;

  ${media.laptop} {
    padding: 0px 0px 10px 0px;
  }
`

const Title = styled.h2`
  margin: 0;
  font-size: var(--font-size-lg);

  ${media.mobile} {
    font-size: var(--font-size-xl);
  }
`

interface IColumn {
  dark?: boolean
}

const Column = styled(Col)<IColumn>`
  padding: 0 20px;
  background: ${({ dark }) => dark && "var(--color-dark)"};
  color: ${({ dark }) => dark && "var(--color-light)"};

  ${media.tablet} {
    padding: 0 30px;
  }
  ${media.laptop} {
    width: 50%;
  }

  &:first-child {
    ${media.laptop} {
      border-right: 1px solid var(--color-grey-3);
    }
  }

  ${Header} {
    border-color: ${({ dark }) => dark && "var(--color-light)"};
  }

  &#projects-column {
    &.events-active {
      display: none;
    }

    ${media.laptop} {
      display: block;
    }
  }
  &#events-column {
    &.events-active {
      display: block;
    }

    ${media.laptop} {
      display: block;
    }
  }
`

const ScrollableContent = styled.div`
  padding: 30px 0;
  overflow-y: auto;
  overflow-x: hidden;
  height: calc(100vh - 50px);

  /* scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Track */
  ::-webkit-scrollbar-track {
    display: none;
  }

  /* Handle */
  ::-webkit-scrollbar-thumb {
    display: none;
  }

  /* Handle on hover */
  ::-webkit-scrollbar-thumb:hover {
    display: none;
  }

  /*Hide scrollbar for IE and Edge */
  -ms-overflow-style: none;
`

const Button = styled.button`
  background: transparent;
  border: none;
`

const Views = styled(Button)`
  font-size: var(--font-size-md);
`

const FiltersDropdown = styled(Dropdown)`
  display: inline-block;

  &.hidden {
    visibility: hidden;
  }

  #toggleFilters {
    background: transparent;
    border: none;
    outline: none;
    box-shadow: none;
    margin-right: -10px;

    &:after {
      content: initial;
    }
  }

  img {
    width: 18px;
  }
`

const DropdownMenu = styled(Dropdown.Menu)`
  padding: 0;
  background-color: var(--color-light-1);
  border: 1px solid var(--color-light-2);
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  left: -10px !important;
  font-size: var(--font-size-sm);

  ${media.mobile} {
    font-size: var(--font-size-md);
  }
`

const DropdownItem = styled(Dropdown.Item)`
  display: flex;
  justify-content: space-between;
  align-content: center;
  border: 0;
  background: inherit;
  font: inherit;
  outline: 0;
  padding: 15px 20px;
  text-align: inherit;
  white-space: nowrap;
  width: 100%;

  span {
    max-width: 310px;
    overflow-x: hidden;
    text-overflow: ellipsis;
  }

  &:not(:first-child) {
    border-top: 1px solid var(--color-light-2);
  }

  &:hover {
    background: var(--color-grey);
  }
  &:focus {
    outline: 0;
  }
  &.active {
    background: var(--color-light-2);
  }
`
const FilterName = styled.span`
  margin-right: 30px;
`
const FilterResults = styled.span`
  color: var(--color-grey-2);
`

const ButtonGroup = styled.div`
  display: flex;
  align-items: center;
  transform: translate(0px, 9px);
  position: relative;
  z-index: 1;
`

type IHandleFilters = React.MouseEvent<HTMLButtonElement, MouseEvent>

interface IProps {
  id?: string
  title: string
  type: "projects" | "events"
  content: any
  dark?: boolean
  views?: string[]
  themes?: ITheme[]
  filters?: IFilter[]
  isEventsActive?: boolean
}

const ScrollableColumn = ({
  id,
  title,
  type,
  content,
  dark,
  views,
  filters,
  isEventsActive,
  themes,
}: IProps) => {
  /**
   * Handle projects views
   * (cards / list)
   */
  const [defaultView, setView] = useState(true)

  /**
   * Handle filters
   */
  const [selectedFilter, setFilter] = useState<string | null>(null)

  const handleFilters = (e: IHandleFilters) => {
    const filterName = e.currentTarget.value

    if (filterName === selectedFilter) {
      setFilter(null)
    } else {
      setFilter(filterName)
    }
  }

  return (
    <Column
      lg
      dark={dark}
      id={id}
      className={isEventsActive ? "events-active" : ""}
    >
      <Header>
        <Title>{title}</Title>
        <ButtonGroup>
          {views && (
            <Views onClick={() => setView(view => !view)}>
              {defaultView ? views[0] : views[1]}
            </Views>
          )}
          {filters && (
            <FiltersDropdown
              className={defaultView ? "" : "hidden"}
              alignRight
            >
              <Dropdown.Toggle id="toggleFilters">
                <img src={burger} alt="filters" />
              </Dropdown.Toggle>

              <DropdownMenu>
                {filters.map(({ name, results }, i: number) => (
                  <DropdownItem
                    as="button"
                    key={i}
                    value={name}
                    onClick={handleFilters}
                    className={
                      selectedFilter === name ? "active" : ""
                    }
                  >
                    <FilterName>{name}</FilterName>
                    {/* <FilterResults>{results}</FilterResults> */}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </FiltersDropdown>
          )}
        </ButtonGroup>
      </Header>
      <ScrollableContent>
        {type === "projects" && (
          <ThumbsProject
            list={content}
            defaultView={defaultView}
            selectedFilter={selectedFilter}
            filters={filters}
            themes={themes}
          />
        )}
        {type === "events" && (
          <ThumbsEvent list={content.list as IEvent[]} />
        )}
      </ScrollableContent>
    </Column>
  )
}

export default ScrollableColumn
